// ============================================================
//  src/pages/ProfilePage.js
// ============================================================
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { listBots, forgotPasswordReset } from '../api/client';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────
function daysLeft(expiryDateStr) {
  if (!expiryDateStr) return null;
  const diff = new Date(expiryDateStr) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function planColor(plan) {
  const p = (plan || '').toLowerCase();
  if (p.includes('pro'))      return { bg: 'rgba(0,44,237,0.08)',  color: 'var(--primary)',  label: 'Pro' };
  if (p.includes('business')) return { bg: 'rgba(0,196,140,0.10)', color: 'var(--success)',  label: 'Business' };
  if (p.includes('enterprise'))return { bg: 'rgba(255,149,0,0.10)',color: 'var(--warning)',  label: 'Enterprise' };
  return                              { bg: 'rgba(155,160,192,0.12)',color: 'var(--text-muted)', label: 'Free' };
}

function countdownColor(days) {
  if (days === null) return 'var(--text-muted)';
  if (days <= 3)  return 'var(--danger)';
  if (days <= 10) return 'var(--warning)';
  return 'var(--success)';
}

// ── Sub-components ────────────────────────────────────────────
function Avatar({ name, size = 72 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--primary) 0%, #6366f1 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.36, fontWeight: 800,
      flexShrink: 0, userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '13px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: 'var(--text-dark)', fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.7px',
      marginBottom: 16, marginTop: 32,
    }}>
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '24px 28px',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Plan countdown ring ───────────────────────────────────────
function CountdownRing({ days, total = 30 }) {
  const radius  = 44;
  const stroke  = 6;
  const norm    = radius - stroke / 2;
  const circ    = 2 * Math.PI * norm;
  const pct     = days === null ? 0 : Math.min(days / total, 1);
  const dash    = pct * circ;
  const color   = countdownColor(days);

  return (
    <svg width={radius * 2} height={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={radius} cy={radius} r={norm} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle
        cx={radius} cy={radius} r={norm}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  );
}

// ── Password reset form ───────────────────────────────────────
function PasswordResetForm({ email }) {
  const [step,     setStep]     = useState('idle'); // idle | code_sent | done
  const [code,     setCode]     = useState('');
  const [newPass,  setNewPass]  = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showNew,  setShowNew]  = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [loading,  setLoading]  = useState(false);

  async function sendCode() {
    setLoading(true);
    try {
      const { data } = await import('../api/client').then(m =>
        m.forgotPasswordReq({ email })
      );
      if (data?.success) {
        setStep('code_sent');
        toast.success('Reset code sent to your email.');
      } else {
        toast.error(data?.message || 'Failed to send code.');
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    }
    setLoading(false);
  }

  async function resetPassword() {
    if (!code.trim())          return toast.error('Enter the code from your email.');
    if (newPass.length < 8)    return toast.error('Password must be at least 8 characters.');
    if (newPass !== confirm)   return toast.error('Passwords do not match.');
    setLoading(true);
    try {
      // Step 1 — verify code
      const { data: vData } = await import('../api/client').then(m =>
        m.forgotPasswordVerify({ email, code })
      );
      if (!vData?.success) { toast.error(vData?.message || 'Invalid code.'); setLoading(false); return; }

      // Step 2 — set new password
      const { data: rData } = await import('../api/client').then(m =>
        m.forgotPasswordReset({ email, code, password: newPass })
      );
      if (rData?.success) {
        setStep('done');
        toast.success('Password updated successfully!');
      } else {
        toast.error(rData?.message || 'Reset failed.');
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    }
    setLoading(false);
  }

  const inputStyle = {
    width: '100%', padding: '11px 14px', fontSize: 13.5,
    border: '1.5px solid var(--border)', borderRadius: 10,
    background: 'var(--surface)', color: 'var(--text-dark)',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  if (step === 'done') {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-dark)', marginBottom: 6 }}>Password updated</div>
        <div style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>Your new password is active. Use it next time you log in.</div>
        <button
          onClick={() => { setStep('idle'); setCode(''); setNewPass(''); setConfirm(''); }}
          style={{ marginTop: 18, background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Change again
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {step === 'idle' && (
        <>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            We'll send a reset code to <strong style={{ color: 'var(--text-dark)' }}>{email}</strong>. Enter it below along with your new password.
          </p>
          <button
            className="btn btn-primary"
            onClick={sendCode}
            disabled={loading}
            style={{ alignSelf: 'flex-start' }}>
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
            Send reset code
          </button>
        </>
      )}

      {step === 'code_sent' && (
        <>
          <div style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 4 }}>
            Check your inbox at <strong style={{ color: 'var(--text-dark)' }}>{email}</strong> and enter the code below.
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>RESET CODE</label>
            <input
              style={{ ...inputStyle, letterSpacing: '0.2em', fontFamily: 'monospace', textTransform: 'uppercase' }}
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Enter code from email"
              maxLength={12}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>NEW PASSWORD</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNew ? 'text' : 'password'}
                style={{ ...inputStyle, paddingRight: 44 }}
                value={newPass}
                onChange={e => setNewPass(e.target.value)}
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14 }}>
                {showNew ? '🙈' : '👁️'}
              </button>
            </div>
            {newPass.length > 0 && (
              <PasswordStrength password={newPass} />
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>CONFIRM PASSWORD</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConf ? 'text' : 'password'}
                style={{
                  ...inputStyle,
                  paddingRight: 44,
                  borderColor: confirm.length > 0 ? (confirm === newPass ? 'var(--success)' : 'var(--danger)') : 'var(--border)',
                }}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                onClick={() => setShowConf(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14 }}>
                {showConf ? '🙈' : '👁️'}
              </button>
            </div>
            {confirm.length > 0 && confirm !== newPass && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>Passwords do not match</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              className="btn btn-primary"
              onClick={resetPassword}
              disabled={loading}
              style={{ flex: 1 }}>
              {loading
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Updating…</>
                : 'Update password'
              }
            </button>
            <button
              className="btn btn-outline"
              onClick={() => { setStep('idle'); setCode(''); setNewPass(''); setConfirm(''); }}
              disabled={loading}>
              Cancel
            </button>
          </div>

          <button
            onClick={sendCode}
            disabled={loading}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', padding: 0 }}>
            Resend code
          </button>
        </>
      )}
    </div>
  );
}

// ── Password strength meter ───────────────────────────────────
function PasswordStrength({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['var(--danger)', 'var(--warning)', 'var(--info)', 'var(--success)'];

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 4,
            background: i < score ? colors[score - 1] : 'var(--border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: colors[score - 1] ?? 'var(--text-muted)' }}>
        {score > 0 ? labels[score - 1] : ''}
      </div>
    </div>
  );
}

// ── Main ProfilePage ──────────────────────────────────────────
export default function ProfilePage() {
  const { user: ctxUser } = useAuth();
  const [bots,    setBots]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listBots()
      .then(({ data }) => setBots(data?.bots ?? []))
      .catch(() => setBots([]))
      .finally(() => setLoading(false));
  }, []);

  // Merge context user with localStorage fallback — handles timing/field-name variations
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('authUser') || '{}'); } catch { return {}; }
  })();
  const user = { ...storedUser, ...(ctxUser || {}) };

  // Support multiple possible field name conventions from the backend
  const firstName = user.sFname  ?? user.fname      ?? user.first_name ?? user.firstName ?? '';
  const lastName  = user.sLname  ?? user.lname      ?? user.last_name  ?? user.lastName  ?? '';
  const fullName  = (`${firstName} ${lastName}`.trim())
    || user.sName  || user.name    || user.fullName  || '';
  const email     = user.sEmail  ?? user.email      ?? '';
  const plan      = user.sPlan   ?? user.plan       ?? 'free';
  const expiry    = user.sPlanExpiry ?? user.sExpiry ?? user.plan_expiry ?? null;
  const joined    = user.sCreated   ?? user.created_at ?? user.createdAt ?? null;
  const accountId = user.sId     ?? user.id         ?? user.userId      ?? null;

  const planInfo = planColor(plan);
  const days     = daysLeft(expiry);
  const botCount = loading ? '…' : (bots?.length ?? 0);
  const activeCount = loading ? '…' : (bots?.filter(b => b.bIsActive == 1).length ?? 0);

  return (
    <div style={{ padding: '36px 40px', maxWidth: 760, margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: 'var(--text-dark)' }}>Profile</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--text-muted)' }}>Manage your account details and security</p>
      </div>

      {/* ── Top identity card ── */}
      <Card style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <Avatar name={fullName} size={72} />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text-dark)' }}>{fullName}</h2>
            <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 10 }}>{email}</div>
            <span style={{
              display: 'inline-block',
              background: planInfo.bg, color: planInfo.color,
              fontSize: 11.5, fontWeight: 800, padding: '3px 12px',
              borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {planInfo.label} Plan
            </span>
          </div>
        </div>
      </Card>

      {/* ── Stats row ── */}
      <SectionTitle>Your Activity</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 0 }}>

        {/* Bot count */}
        <Card style={{ textAlign: 'center', padding: '20px 16px' }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>{botCount}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Bots</div>
          {!loading && (
            <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>{activeCount} active</div>
          )}
        </Card>

        {/* Plan */}
        <Card style={{ textAlign: 'center', padding: '20px 16px' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: planInfo.color, lineHeight: 1 }}>{planInfo.label}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Plan</div>
          {expiry && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Expires {new Date(expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          )}
        </Card>

        {/* Countdown */}
        <Card style={{ textAlign: 'center', padding: '20px 16px', position: 'relative', overflow: 'hidden' }}>
          {days !== null ? (
            <>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: 6 }}>
                <CountdownRing days={days} total={30} />
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexDirection: 'column',
                }}>
                  <span style={{ fontSize: 18, fontWeight: 900, color: countdownColor(days), lineHeight: 1 }}>{days}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>days</span>
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {days === 0 ? '⚠️ Expired' : days <= 3 ? '⚠️ Expiring soon' : 'Plan Remaining'}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 28, marginBottom: 6 }}>∞</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>No Expiry</div>
            </>
          )}
        </Card>

      </div>

      {/* ── Account info ── */}
      <SectionTitle>Account Details</SectionTitle>
      <Card>
        <InfoRow label="Full Name"  value={fullName} />
        <InfoRow label="Email"      value={email} mono />
        <InfoRow label="Account ID" value={accountId ? `#${accountId}` : '—'} mono />
        <InfoRow label="Joined"     value={joined ? new Date(joined).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'} />
        <div style={{ padding: '13px 0 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Plan</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                background: planInfo.bg, color: planInfo.color,
                fontSize: 11.5, fontWeight: 800, padding: '3px 12px',
                borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{planInfo.label}</span>
              <a href="/billing" style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }}>
                Upgrade →
              </a>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Password reset ── */}
      <SectionTitle>Security</SectionTitle>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(0,44,237,0.08)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>🔑</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-dark)' }}>Change Password</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>A reset code will be sent to your email</div>
          </div>
        </div>
        <PasswordResetForm email={email} />
      </Card>

      <div style={{ height: 48 }} />
    </div>
  );
}